import { useLoginStore } from '@/login/stores/useLoginStore';
import { queryOptions } from '@tanstack/vue-query';
import { toValue, type MaybeRefOrGetter } from 'vue';
import type { Collection} from '@/newApi/services/ResgateManager';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import type { PayBand, PayBandLevel } from '@sysarb/sysarb-types/services/v1/job';
import type { CountryCode, EmploymentSalaryType } from '@sysarb/sysarb-types/general/enums';

type JobPayBandsOptions = {
    queries: {
        getJobPayBands: (subscribe?: boolean) => ReturnType<typeof queryOptions<Collection<PayBand> | null, Error, PayBand[]>>,
        getJobsPayband: (country: MaybeRefOrGetter<CountryCode>, subscribe?: boolean) => ReturnType<typeof queryOptions<PayBand | null, Error, PayBand>>,
        getJobPaybandLevel: (country: MaybeRefOrGetter<CountryCode>, level: MaybeRefOrGetter<number>, salaryType?: MaybeRefOrGetter<EmploymentSalaryType>, subscribe?: boolean) => ReturnType<typeof queryOptions<PayBandLevel | null, Error, PayBandLevel>>,
    },
};

export const jobPayBands: JobPayBandsOptions = {
    queries: {
        getJobPayBands: (subscribe: boolean = true): ReturnType<typeof queryOptions<Collection<PayBand> | null, Error, PayBand[]>> => {
            const { instanceTopic } = useLoginStore();
            const resgate = ResgateManager.getInstance();
            const topic = `v1.${instanceTopic}.jobs.paybands`;
            return queryOptions<Collection<PayBand> | null, Error, PayBand[]>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['paybands', topic],
                queryFn: async (): Promise<Collection<PayBand> | null> => {
                    return resgate.collection<PayBand>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                select: (data) => data?.list ?? [],
            });
        },
        getJobsPayband: <TData = PayBand | null>(country: MaybeRefOrGetter<CountryCode>, subscribe?: boolean): ReturnType<typeof queryOptions<PayBand | null, Error, TData>> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.jobs.payband.${toValue(country)}`;
            return queryOptions<PayBand | null, Error, TData>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['payband', topic],
                queryFn: async (): Promise<PayBand | null> => {
                    return ResgateManager.model<PayBand>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: instanceTopic != null,
            });
        },
        getJobPaybandLevel: <TData = PayBandLevel | null>(country: MaybeRefOrGetter<CountryCode>, level: MaybeRefOrGetter<number>, salaryType?: MaybeRefOrGetter<EmploymentSalaryType>, subscribe?: boolean): ReturnType<typeof queryOptions<PayBandLevel | null, Error, TData>> => {
            const { instanceTopic } = useLoginStore();
            let queryParams = '';

            if (salaryType != null) queryParams += `?salaryType=${toValue(salaryType)}`;

            const topic = `v1.${instanceTopic}.jobs.payband.${toValue(country)}.${toValue(level)}${queryParams}`;
            return queryOptions<PayBandLevel | null, Error, TData>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['job', topic],
                queryFn: async (): Promise<PayBandLevel | null> => {
                    return ResgateManager.model<PayBandLevel>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: instanceTopic != null,
            });
        },
    },
};
